import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Button,
  TextField,
  Typography,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  CircularProgress,
  TablePagination
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import { ReactComponent as BulkUploadIcon } from '../../assets/svg/bulk_upload.svg';
import { useQuery, gql, useLazyQuery } from "@apollo/client";
import BulkUploadDealers from "./BulkUploadDealers";
import AddNewDealers from "./AddNewDealers";
import EditDealersDialog from "./EditDealersDialog";
import DeleteDealersDialog from "./DeleteDealersDialog";
import { useAuth } from "../../hooks/useAuth";
// Define cell style
const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
};
// Define types
export interface Dealer {
  dealer_id: string;
  sapcustomerid: string;
  dealer_name: string;
  dealer_code: string;
  branch_code: string;
  dealer_email: string;
  address_line_1: string;
  gst: string;
  pan: string;
  country: string;
  status: string;
  br: string;
  dlr_category: string;
  dlrtype: string;
  contactno: string;
  phone_no1: string;
  servicezone: string;
  region: string;
  city_classification: string;
  createdate: string;
  sales_number: string;
  service_number: string;
  service_email: string;
  sales_email: string;
}
interface DealerData {
  totalCount: number;
  data: Dealer[];
}
type DealerSearchType = 'DEALER_NAME' | 'DEALER_CODE';
const GET_ALL_DEALERS = gql`
  query GetAllDealer($pageSize: String!, $page: String!) {
    getAllDealer(pageSize: $pageSize, page: $page) {
      totalCount
      data {
        dealer_id
        sapcustomerid
        dealer_name
        dealer_code
        branch_code
        dealer_email
        address_line_1
        gst
        pan
        country
        status
        br
        dlr_category
        dlrtype
        contactno
        phone_no1
        servicezone
        region
        city_classification
        createdate
        sales_number
        service_number
        service_email
        sales_email
      }
    }
  }
`;
const SEARCH_DEALERS = gql`
  query Query($searchType: DealerSearchType!, $searchItem: String!) {
    searchDealer(searchType: $searchType, searchItem: $searchItem) {
      dealer_id
      sapcustomerid
      dealer_name
      dealer_code
      branch_code
      dealer_email
      address_line_1
      gst
      pan
      country
      status
      br
      dlr_category
      dlrtype
      contactno
      phone_no1
      servicezone
      region
      city_classification
      createdate
      sales_number
      service_number
      service_email
      sales_email
    }
  }
`;
const DOWNLOAD_DEALER_INVENTORY = gql`
  query DownloadDealerInventory {
    downloadDealerInventory {
      url
    }
  }
`;
const Dealers: React.FC = () => {
  const auth = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchType, setSearchType] = useState<DealerSearchType>('DEALER_CODE');
  const [bulkUploadOpen, setBulkUploadOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { loading, error, data, refetch } = useQuery<{ getAllDealer: DealerData }>(GET_ALL_DEALERS, {
    variables: {
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString() 
    }
  });
  const [searchDealers, {
    loading: searchLoading,
    data: searchData,
    error: searchError
  }] = useLazyQuery<{ searchDealer: Dealer[] }>(SEARCH_DEALERS);
  const [downloadInventory, { loading: downloadLoading }] = useLazyQuery(DOWNLOAD_DEALER_INVENTORY);
  const dealerTableHeaders: string[] = [
    'Sapcustomer Id',
    'Dealer Name',
    'Dealer Code',
    'Branch Code',
    'Dealer Email',
    'Address Line',
    'GST',
    'PAN',
    'Country',
    'Status',
    'Branch',
    'Dealer Category',
    'Dealer Type',
    'Contact No',
    'Phone No',
    'Service Zone',
    'Region',
    'City Classification',
    'Create Date',
    'Sales Number',
    'Service Number',
    'Service Email',
    'Sales Email',
    ...(auth.canCreate("DEALER") || auth.canUpdate("DEALER") || auth.canDelete("DEALER") ? ['Actions'] : [])
  ];
  const [dealersList, setDealersList] = useState<Dealer[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const handleEditDealer = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setEditDialogOpen(true);
  };
  const handleDeleteDealer = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setDeleteDialogOpen(true);
  };
  const handleEditSaveSuccess = (updated: Dealer) => {
    setEditDialogOpen(false);
    setSelectedDealer(null);
    setSearchTerm("");
    setSearchType('DEALER_CODE');
    refetch({
      pageSize: rowsPerPage.toString(),
      page: (page + 1).toString()
    });
  };
  // On delete success
  const handleDeleted = () => {
    if (selectedDealer) {
      setDealersList(prev => prev.filter(d => d.dealer_id !== selectedDealer.dealer_id));
      setTotalCount(prev => prev - 1);
    }
    setDeleteDialogOpen(false);
    setSelectedDealer(null);
  };
  // Handler for AddNewDealers save
  const handleSaveDealer = (newDealer: any) => {
    setDealersList(prev => [newDealer, ...prev]);
    setTotalCount(prev => prev + 1);
  };
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  // Map UI searchType to server values
  const searchTypeMap = {
    'DEALER_CODE': 'dealer_code',
    'DEALER_NAME': 'dealer_name'
  } as const;
  // Handle search logic
  const performSearch = useCallback(async () => {
    if (!debouncedSearchTerm.trim()) {
      if (data?.getAllDealer) {
        setDealersList(data.getAllDealer.data);
        setTotalCount(data.getAllDealer.totalCount);
      }
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const { data: searchResult } = await searchDealers({
        variables: {
          searchType: searchTypeMap[searchType],
          searchItem: debouncedSearchTerm.trim()
        }
      });
      setDealersList(searchResult?.searchDealer || []);
      setTotalCount(searchResult?.searchDealer?.length || 0);
    } catch (err) {
      console.error("Search error:", err);
      setDealersList([]);
      setTotalCount(0);
    } finally {
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, searchType, searchDealers, data]);
  // Initial data load and when search criteria changes
  useEffect(() => {
    if (data?.getAllDealer && !debouncedSearchTerm) {
      setDealersList(data.getAllDealer.data);
      setTotalCount(data.getAllDealer.totalCount);
    }
  }, [data, debouncedSearchTerm]);
  // Trigger search when debounced term or type changes
  useEffect(() => {
    performSearch();
  }, [debouncedSearchTerm, searchType, performSearch]);
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };
  // Refetch data when page or rowsPerPage changes
  useEffect(() => {
    if (!debouncedSearchTerm.trim()) {
      refetch({
        pageSize: rowsPerPage.toString(),
        page: (page + 1).toString()
      });
    }
  }, [page, rowsPerPage, refetch, debouncedSearchTerm]);
  // Download Dealer Sample File
  const handleDownloadSample = (): void => {
    const link = document.createElement("a");
    link.href = "/dealer_info_template.xlsx";
    link.download = "Dealer-Upload-Sample.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Download all table data as Excel
  const handleDownloadAll = async (): Promise<void> => {
    try {
      const { data } = await downloadInventory();
      if (data?.downloadDealerInventory?.url) {
        const link = document.createElement('a');
        link.href = data.downloadDealerInventory.url;
        link.setAttribute('download', 'Dealer_List.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading inventory:', error);
    }
  };
  const handleBulkUploadSuccess = (): void => {
    setBulkUploadOpen(false);
    refetch();
  };
  // Format epoch seconds to dd-MM-yyyy HH:mm:ss
  const formatEpochIST = (epochTime: number | string): string => {
    if (!epochTime) return '';
    const ts = typeof epochTime === 'string' ? parseInt(epochTime, 10) : epochTime;
    if (isNaN(ts)) return '';
    // Convert to milliseconds and add IST offset (5:30 hours)
    const date = new Date((ts > 1e12 ? ts : ts * 1000) + 5.5 * 60 * 60 * 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} / 
      ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  };
  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        Dealers
      </Typography>
      {(error || searchError) && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error ? `Error loading dealers: ${error.message}` : `Search error: ${searchError?.message}`}
          </Alert>
        </Box>
      )}
      <AddNewDealers
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleSaveDealer}
      />
      <BulkUploadDealers
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploaded={handleBulkUploadSuccess}
      />
      <Card sx={{
        background: '#fff',
        borderRadius: 2,
        boxShadow: 1,
        p: 0,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        minHeight: 100,
        overflow: 'hidden',
      }}>
        {/* {(loading || searchLoading) && <LinearProgress />} */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: '#fff',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            boxShadow: '0 2px 8px -4px #e0e0e0',
            px: { xs: 1, sm: 2 },
            pt: 2,
            pb: 2,
            left: 0,
            right: 0,
            width: '100%',
            minWidth: 0,
            overflow: 'visible',
          }}
        >
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Box
              display="flex"
              alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }}
              sx={{ minWidth: 60, width: '100%', gap: 2 }}
            >
              <Box
                display="flex"
                gap={1}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
              >
                <TextField
                  select
                  size="small"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value as DealerSearchType)}
                  sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
                >
                  <MenuItem value="DEALER_CODE">Search by Dealer Code</MenuItem>
                  <MenuItem value="DEALER_NAME">Search by Dealer Name</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  placeholder={
                    searchType === 'DEALER_CODE' ? 'Enter Dealer Code' :
                      'Enter Dealer Name'
                  }
                  variant="outlined"
                  sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                  inputProps={{ maxLength: 32 }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  sx={{ ml: 1, color: '#000', flex: '0 0 auto' }}
                  onClick={handleDownloadAll}
                  disabled={downloadLoading}
                >
                  <Tooltip title="Export to Excel" placement="top" arrow>
                    {downloadLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                  </Tooltip>
                </IconButton>
              </Box>
              <Box display="flex" gap={2} flexWrap={{ xs: 'wrap', sm: 'nowrap' }} sx={{ mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 'auto' } }}>
                {(auth.canUpdate("DEALER") || auth.canCreate("DEALER")) && (
                  <>
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#000',
                        color: '#fff',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover': { backgroundColor: '#222' },
                        '.MuiButton-startIcon': { color: '#fff' }
                      }}
                      startIcon={<InsertDriveFileOutlined />}
                      onClick={handleDownloadSample}
                    >
                      Sample File
                    </Button>
                  </>
                )}
                {auth.canCreate("DEALER") && (
                  <>
                    <Button
                      variant="contained"
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setAddOpen(true)}
                    >
                      Add New Dealer
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<BulkUploadIcon />}
                      sx={{
                        color: '#fff',
                        backgroundColor: '#E60012',
                        '&:hover': { backgroundColor: '#cc0010' },
                        textTransform: 'none',
                        borderRadius: '8px',
                        paddingX: 2,
                        paddingY: 1,
                        minWidth: 120,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                      onClick={() => setBulkUploadOpen(true)}
                    >
                      Bulk Dealer Upload
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, px: { xs: 0.5, sm: 2 }, py: 2, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <Box sx={{ width: '100%', overflowX: 'auto', flex: 1 }}>
            <Table sx={{
              minWidth: 600,
              background: '#fff',
              width: '100%',
              tableLayout: 'auto',
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}>
              <TableHead>
                <TableRow>
                  {dealerTableHeaders.map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{
                        background: '#E60012',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        whiteSpace: 'nowrap',
                        padding: { xs: '6px', sm: '10px' },
                        borderBottom: '1.5px solid #e0e0e0',
                        borderRight: '2px solid rgba(255, 255, 255, 1)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '&:last-child': {
                          borderRight: 'none',
                        },
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(loading || searchLoading) ? (
                  <TableRow>
                    <TableCell colSpan={dealerTableHeaders.length} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : dealersList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={dealerTableHeaders.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                      {isSearching ? 'No matching dealers found' : 'No dealer data available'}
                    </TableCell>
                  </TableRow>
                ) : (
                  dealersList.map((dealer) => (
                    <TableRow key={dealer.dealer_id}>
                      {Object.entries({
                        sapcustomerid: dealer.sapcustomerid || "N/A",
                        dealer_name: dealer.dealer_name || "N/A",
                        dealer_code: dealer.dealer_code || "N/A",
                        branch_code: dealer.branch_code || "N/A",
                        dealer_email: dealer.dealer_email || "N/A",
                        address_line_1: dealer.address_line_1 || "N/A",
                        gst: dealer.gst || "N/A",
                        pan: dealer.pan || "N/A",
                        country: dealer.country || "N/A",
                        status: dealer.status || "N/A",
                        br: dealer.br || "N/A",
                        dlr_category: dealer.dlr_category || "N/A",
                        dlrtype: dealer.dlrtype || "N/A",
                        contactno: dealer.contactno || "N/A",
                        phone_no1: dealer.phone_no1 || "N/A",
                        servicezone: dealer.servicezone || "N/A",
                        region: dealer.region || "N/A",
                        city_classification: dealer.city_classification || "N/A",
                        createdate: formatEpochIST(dealer.createdate) || "N/A",
                        sales_number: dealer.sales_number || "N/A",
                        service_number: dealer.service_number || "N/A",
                        service_email: dealer.service_email || "N/A",
                        sales_email: dealer.sales_email || "N/A"
                      }).map(([key, value]) => (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            fontSize: '14px',
                            padding: '8px',
                            width: 'auto',
                            borderBottom: '1px solid #e0e0e0',
                            borderRight: '1px solid #e0e0e0',
                            color: '#000',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            '&:last-child': {
                              borderRight: 'none',
                            },
                          }}
                        >
                          <Tooltip title={value} placement="top" arrow>
                            <span>{value}</span>
                          </Tooltip>
                        </TableCell>
                      ))}
                      {(auth.canCreate("DEALER") || auth.canUpdate("DEALER") || auth.canDelete("DEALER")) && (
                        <TableCell align="center" sx={{ minWidth: 120 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            {auth.canUpdate("DEALER") && (
                              <IconButton size="small" onClick={() => handleEditDealer(dealer)}
                                sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {auth.canDelete("DEALER") && (
                              <IconButton size="small" onClick={() => handleDeleteDealer(dealer)}
                                sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
          {/* Edit Dealer Dialog */}
          <EditDealersDialog
            open={editDialogOpen}
            onClose={() => { setEditDialogOpen(false); setSelectedDealer(null); }}
            dealer={selectedDealer || undefined}
            onSaveSuccess={handleEditSaveSuccess}
          />
          {/* Delete Dealer Dialog */}
          <DeleteDealersDialog
            open={deleteDialogOpen}
            onClose={() => { setDeleteDialogOpen(false); setSelectedDealer(null); }}
            dealer={selectedDealer || {}}
            onDeleted={handleDeleted}
          />
          {!debouncedSearchTerm.trim() && (
            <Box sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 20,
              background: '#fff',
              borderTop: '1px solid #e0e0e0',
              pt: 2,
              mt: 'auto',
              overflowX: 'auto',
            }}>
              <TablePagination
                rowsPerPageOptions={[20, 50, 100, 500]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                sx={{
                  minWidth: '600px',
                  '.MuiTablePagination-toolbar': {
                    flexWrap: 'nowrap',
                    justifyContent: { xs: 'center', sm: 'space-between' },
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};
export default Dealers;